import { getDotColor, getDotFill } from '@odo/shared-ui';
import type { DmNotificationResolution } from '../../../services/dmNotifications';
import type { DmMessageFrustration, DmMessageUrgency } from '../../../services/dmMessagesInbox';
import type {
    OverviewAgeBucket,
    OverviewCategory,
    OverviewLevel
} from '../../../services/dmMonitoringOverview';
import type { BacklogKey } from './backlogRows';
import { FRUSTRATION_META, toFrustrationOption } from '../messages/triage/frustration';
import { PRIORITY_LEVELS } from '../../work-queue/priority/priority';

export interface LegendEntry {
    label: string;
    // Tailwind background class for the bar segment and its swatch.
    swatch: string;
}

// Pie slices paint with SVG fill, so each slice tone carries a fill class beside its swatch.
export interface SliceTone {
    swatch: string;
    fill: string;
}

export type SliceLegendEntry = LegendEntry & SliceTone;

const BRAND_TEAL = 'bg-[#007E87]';
const BRAND_TEAL_HEX = '#007E87';

// Real levels share the inbox's dot colours; Other and No level step up in lightness.
export const LEVEL_LEGEND: Record<OverviewLevel, SliceLegendEntry> = {
    alert: { label: 'Alert', swatch: getDotColor('alert'), fill: getDotFill('alert') },
    warning: { label: 'Warning', swatch: getDotColor('warning'), fill: getDotFill('warning') },
    info: { label: 'Info', swatch: getDotColor('info'), fill: getDotFill('info') },
    other: { label: 'Other', swatch: 'bg-gray-400', fill: 'fill-gray-400' },
    none: { label: 'No level', swatch: 'bg-gray-300', fill: 'fill-gray-300' }
};

// By rank, alternating dark and light so neighbouring slices separate; no red, amber or blue,
// which the levels pie beside it uses.
const EVENT_TYPE_TONES: readonly SliceTone[] = [
    { swatch: 'bg-teal-700', fill: 'fill-teal-700' },
    { swatch: 'bg-sky-300', fill: 'fill-sky-300' },
    { swatch: 'bg-indigo-600', fill: 'fill-indigo-600' },
    { swatch: 'bg-lime-300', fill: 'fill-lime-300' },
    { swatch: 'bg-fuchsia-700', fill: 'fill-fuchsia-700' },
    { swatch: 'bg-cyan-200', fill: 'fill-cyan-200' }
];

export const EVENT_TYPE_TOP_COUNT = EVENT_TYPE_TONES.length;

export const OTHER_EVENT_TYPES_LEGEND: SliceLegendEntry = {
    label: 'Other types',
    swatch: 'bg-gray-400',
    fill: 'fill-gray-400'
};

export function rankedSliceTone(rank: number): SliceTone {
    return EVENT_TYPE_TONES[rank % EVENT_TYPE_TONES.length];
}

// Shown even at zero; the rest only when DM sent some.
export const ALWAYS_SHOWN_LEVELS: readonly OverviewLevel[] = ['alert', 'warning', 'info'];

export const CATEGORY_LEGEND: Record<OverviewCategory, LegendEntry> = {
    notification: { label: 'Notifications', swatch: BRAND_TEAL },
    message: { label: 'Messages', swatch: 'bg-teal-200' }
};

// Hex for recharts; messages are indigo-500, as teal-200 caps vanish on white and on the shade.
export const BACKLOG_LEGEND: Record<BacklogKey, { label: string; color: string }> = {
    notifications: { label: CATEGORY_LEGEND.notification.label, color: BRAND_TEAL_HEX },
    messages: { label: CATEGORY_LEGEND.message.label, color: '#6366f1' }
};

export const UNRELIABLE_SHADE = { fill: '#e5e7eb', fillOpacity: 0.6 } as const;

export const AGE_BUCKET_LABELS: Record<OverviewAgeBucket, string> = {
    under1d: 'Under 1 day',
    '1to3d': '1 to 3 days',
    '3to7d': '3 to 7 days',
    over7d: 'Over 7 days'
};

export const OVERDUE_AGE_BUCKET: OverviewAgeBucket = 'over7d';

export const RESOLUTION_ORDER: readonly DmNotificationResolution[] = [
    'dashboard',
    'dm',
    'gone',
    'excluded'
];

// Hex, since recharts paints with fill: brand teal, then slate-700, slate-500 and slate-300.
export const RESOLUTION_LEGEND: Record<DmNotificationResolution, { label: string; color: string }> =
    {
        dashboard: { label: 'Reviewed here', color: BRAND_TEAL_HEX },
        dm: { label: 'Resolved in DM', color: '#334155' },
        gone: { label: 'Gone from DM', color: '#64748b' },
        excluded: { label: 'Excluded', color: '#cbd5e1' }
    };

// Green, amber, red as on the status pills; amber is lighter and red darker than green.
const TRIAGE_TONES = {
    calm: { swatch: 'bg-green-500', fill: 'fill-green-500' },
    raised: { swatch: 'bg-amber-400', fill: 'fill-amber-400' },
    urgent: { swatch: 'bg-red-600', fill: 'fill-red-600' },
    unset: { swatch: 'bg-gray-300', fill: 'fill-gray-300' }
} as const satisfies Record<string, SliceTone>;

const PRIORITY_TONES: Record<DmMessageUrgency, SliceTone> = {
    normal: TRIAGE_TONES.calm,
    high: TRIAGE_TONES.raised,
    extreme: TRIAGE_TONES.urgent
};

export function priorityLegend(value: DmMessageUrgency): SliceLegendEntry {
    return { label: PRIORITY_LEVELS[value].label, ...PRIORITY_TONES[value] };
}

const FRUSTRATION_TONES: Record<ReturnType<typeof toFrustrationOption>, SliceTone> = {
    low: TRIAGE_TONES.calm,
    medium: TRIAGE_TONES.raised,
    high: TRIAGE_TONES.urgent,
    unset: TRIAGE_TONES.unset
};

export function frustrationLegend(value: DmMessageFrustration): SliceLegendEntry {
    const option = toFrustrationOption(value);
    return { label: FRUSTRATION_META[option].label, ...FRUSTRATION_TONES[option] };
}
